/**
 * Feb 2026 R7 · Public client-facing questionnaire form.
 * Route: /q/:token  (no auth — token is the credential).
 *
 * Supports:
 *   • Single questionnaire OR master wizard (multiple grouped Q sets).
 *   • Optional OTP email verification before the form appears.
 *   • Autosave every 4s + on-blur.
 *   • All 9 question types with client-side required-field validation.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, ArrowRight, ArrowLeft, Check, Lock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND}/api/v1` });

export default function PublicRequirementBrief() {
  const { token } = useParams();
  const [state, setState] = useState({ kind: null, email_required: false });
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});   // question_id -> value
  const [respondent, setRespondent] = useState({ name: "", email: "" });
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [sessionToken, setSessionToken] = useState(() =>
    localStorage.getItem(`q-session-${token}`) || "");
  const [phase, setPhase] = useState("loading"); // loading | otp | form | done
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const autosaveTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await publicApi.get(
        `/public/requirement-briefs/${token}`,
        { params: sessionToken ? { session_token: sessionToken } : {} });
      setState(data);
      setPhase("form");
    } catch (e) {
      if (e.response?.status === 401 && state.email_required !== false) {
        setState((s) => ({ ...s, email_required: true }));
        setPhase("otp");
      } else if (e.response?.status === 410) {
        setPhase("expired");
      } else if (e.response?.status === 404) {
        // First-touch: try to detect if OTP is required by hitting without session token
        try {
          await publicApi.get(`/public/requirement-briefs/${token}`);
          setPhase("expired");
        } catch (er) {
          if (er.response?.status === 401) { setPhase("otp"); }
          else if (er.response?.status === 410) { setPhase("expired"); }
          else { toast.error(apiErrorMessage(er, "Not found")); setPhase("expired"); }
        }
      } else {
        toast.error(apiErrorMessage(e, "Failed to load"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, sessionToken]);
  useEffect(() => { load(); }, [load]);

  const requestOtp = async () => {
    if (!otpEmail.trim()) return;
    setBusy(true);
    try {
      await publicApi.post(`/public/requirement-briefs/${token}/otp/request`, { email: otpEmail.trim() });
      setOtpSent(true);
      toast.success(`Code sent to ${otpEmail}`);
    } catch (e) { toast.error(apiErrorMessage(e, "OTP send failed")); }
    finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    setBusy(true);
    try {
      const { data } = await publicApi.post(
        `/public/requirement-briefs/${token}/otp/verify`,
        { email: otpEmail.trim(), code: otpCode.trim() });
      setSessionToken(data.session_token);
      localStorage.setItem(`q-session-${token}`, data.session_token);
      setRespondent((r) => ({ ...r, email: otpEmail.trim() }));
      toast.success("Verified");
      // reload
      const { data: full } = await publicApi.get(
        `/public/requirement-briefs/${token}`,
        { params: { session_token: data.session_token } });
      setState(full);
      setPhase("form");
    } catch (e) { toast.error(apiErrorMessage(e, "Wrong code")); }
    finally { setBusy(false); }
  };

  const questionnaires = state.kind === "master_rfq" ? (state.wizard || [])
                                : state.questionnaire ? [state.questionnaire] : [];
  const currentQ = questionnaires[step];

  // Prefill answers when questionnaire loads
  useEffect(() => {
    if (currentQ && currentQ.questions) {
      setAnswers((prev) => {
        const next = { ...prev };
        for (const q of currentQ.questions) {
          if (next[q.id] === undefined && q.prefill != null) {
            next[q.id] = q.prefill;
          }
        }
        return next;
      });
    }
  }, [currentQ?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave every 4s while typing
  useEffect(() => {
    if (!currentQ || phase !== "form") return;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        await publicApi.post(
          `/public/requirement-briefs/${token}/autosave`,
          { questionnaire_id: currentQ.id,
            respondent_name: respondent.name || null,
            respondent_email: respondent.email || null,
            answers },
          { params: sessionToken ? { session_token: sessionToken } : {} });
      } catch { /* silent */ }
    }, 4000);
    return () => clearTimeout(autosaveTimer.current);
  }, [answers, currentQ, phase, respondent, sessionToken, token]);

  const submitSection = async () => {
    if (!currentQ) return;
    // client-side required check
    const missing = currentQ.questions.filter((q) => {
      if (!q.required) return false;
      const v = answers[q.id];
      return v === undefined || v === null || v === ""
              || (Array.isArray(v) && v.length === 0);
    });
    if (missing.length) {
      toast.error(`Please answer: ${missing.slice(0, 2).map((x) => x.text).join(" · ")}${missing.length > 2 ? "…" : ""}`);
      return;
    }
    setBusy(true);
    try {
      await publicApi.post(
        `/public/requirement-briefs/${token}/submit`,
        { questionnaire_id: currentQ.id,
          respondent_name: respondent.name || null,
          respondent_email: respondent.email || null,
          answers },
        { params: sessionToken ? { session_token: sessionToken } : {} });
      if (step < questionnaires.length - 1) {
        setStep((s) => s + 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setPhase("done");
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Submit failed")); }
    finally { setBusy(false); }
  };

  if (phase === "loading") {
    return <PublicShell><div className="text-sm">Loading…</div></PublicShell>;
  }
  if (phase === "expired") {
    return (
      <PublicShell>
        <div className="text-center py-16">
          <Lock className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <h2 className="text-lg font-semibold mb-2">Link unavailable</h2>
          <p className="text-sm text-muted-foreground">This questionnaire link is invalid, expired, or has been revoked. Ask the agency to send a fresh link.</p>
        </div>
      </PublicShell>
    );
  }
  if (phase === "done") {
    return (
      <PublicShell>
        <div className="text-center py-16">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
            <Check className="w-6 h-6 text-emerald-600" />
          </div>
          <h2 className="text-lg font-semibold mb-2">Thanks — your responses are in.</h2>
          <p className="text-sm text-muted-foreground">The agency has been notified. They may reach out with follow-up questions.</p>
        </div>
      </PublicShell>
    );
  }
  if (phase === "otp") {
    return (
      <PublicShell>
        <div className="max-w-md mx-auto py-10 space-y-4">
          <div>
            <h2 className="text-xl font-semibold mb-1">Verify your email</h2>
            <p className="text-xs text-muted-foreground">The agency has locked this link to email OTP verification.</p>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Your email</Label>
            <Input value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)}
                      type="email" className="rounded-none h-10" data-testid="rb-otp-email" />
          </div>
          {!otpSent ? (
            <Button className="w-full rounded-none h-10" onClick={requestOtp} disabled={busy || !otpEmail.trim()} data-testid="rb-otp-request">
              {busy ? "Sending…" : "Send code"}
            </Button>
          ) : (
            <>
              <div>
                <Label className="text-xs mb-1 block">6-digit code</Label>
                <Input value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                          maxLength={6} className="rounded-none h-10 font-mono tracking-widest" data-testid="rb-otp-code" />
              </div>
              <Button className="w-full rounded-none h-10" onClick={verifyOtp} disabled={busy || !otpCode.trim()} data-testid="rb-otp-verify">
                {busy ? "Verifying…" : "Verify"}
              </Button>
              <button onClick={requestOtp} className="text-[11px] text-primary underline">Resend code</button>
            </>
          )}
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <div className="max-w-3xl mx-auto py-6 space-y-4">
        {state.kind === "master_rfq" && questionnaires.length > 1 && (
          <div className="flex items-center gap-1 mb-2" data-testid="rb-wizard-progress">
            {questionnaires.map((_, i) => (
              <div key={i} className={`flex-1 h-1 ${i <= step ? "bg-primary" : "bg-border"}`} />
            ))}
            <span className="text-[10px] text-muted-foreground ml-2">
              Section {step + 1} of {questionnaires.length}
            </span>
          </div>
        )}
        <h1 className="text-2xl font-semibold" data-testid="rb-title">{currentQ?.title}</h1>
        {currentQ?.description && (
          <p className="text-sm text-muted-foreground">{currentQ.description}</p>
        )}
        {!sessionToken && !state.email_required && (
          <div className="grid grid-cols-2 gap-2 border border-border p-3">
            <div>
              <Label className="text-xs mb-1 block">Your name</Label>
              <Input value={respondent.name} onChange={(e) => setRespondent({ ...respondent, name: e.target.value })}
                       className="h-9 rounded-none" data-testid="rb-resp-name" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Your email</Label>
              <Input value={respondent.email} onChange={(e) => setRespondent({ ...respondent, email: e.target.value })}
                       type="email" className="h-9 rounded-none" data-testid="rb-resp-email" />
            </div>
          </div>
        )}
        <GroupedFields
          groups={currentQ?.groups || []}
          questions={currentQ?.questions || []}
          answers={answers}
          onChange={(qid, v) => setAnswers((a) => ({ ...a, [qid]: v }))}
        />
        <div className="flex items-center gap-2 pt-4 border-t border-border">
          {step > 0 && (
            <Button variant="outline" className="rounded-none" onClick={() => setStep((s) => s - 1)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          <div className="flex-1" />
          <Button onClick={submitSection} disabled={busy} className="rounded-none" data-testid="rb-submit">
            {busy ? "Saving…" : (
              step < questionnaires.length - 1
                ? <>Next <ArrowRight className="w-4 h-4 ml-1" /></>
                : <>Submit <Check className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        </div>
      </div>
    </PublicShell>
  );
}

function FileField({ question, value, onChange, testid, label, help }) {
  const { token } = useParams();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Max 25 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const sess = localStorage.getItem(`q-session-${token}`);
      const { data } = await publicApi.post(
        `/public/requirement-briefs/${token}/upload`,
        fd,
        { params: sess ? { session_token: sess } : {},
          headers: { "Content-Type": "multipart/form-data" } });
      onChange({ url: data.url, filename: data.filename, size: data.size });
      toast.success(`Uploaded ${data.filename}`);
    } catch (e2) { toast.error(apiErrorMessage(e2, "Upload failed")); }
    finally { setBusy(false); }
  };
  return (
    <div>{label}{help}
      <div className="border border-dashed border-border p-4 space-y-2" data-testid={testid}>
        {value?.filename ? (
          <div className="text-xs">
            <span className="chip chip-approved text-[10px] mr-2">Uploaded</span>
            <span className="font-mono">{value.filename}</span>
            <button onClick={() => onChange(null)} className="text-rose-600 ml-2 text-[11px]">Replace</button>
          </div>
        ) : (
          <input type="file" onChange={upload} disabled={busy} className="text-xs" />
        )}
        {busy && <div className="text-[11px] text-muted-foreground">Uploading…</div>}
      </div>
    </div>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" />
      <div className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-xs uppercase tracking-widest">Requirements form</span>
        </div>
      </div>
      <div className="px-4 sm:px-6">{children}</div>
    </div>
  );
}

function GroupedFields({ groups, questions, answers, onChange }) {
  // Bucket questions by group_id preserving order
  const buckets = { __ungrouped__: [] };
  (groups || []).forEach((g) => { buckets[g.id] = []; });
  questions.forEach((q) => {
    const key = q.group_id && buckets[q.group_id] ? q.group_id : "__ungrouped__";
    buckets[key].push(q);
  });
  const orderedGroups = groups || [];
  // Global question index (used for numbering in labels)
  const indexOf = (qid) => questions.findIndex((x) => x.id === qid);

  const renderField = (q) => (
    <FieldRenderer
      key={q.id}
      index={indexOf(q.id)}
      question={q}
      value={answers[q.id]}
      onChange={(v) => onChange(q.id, v)}
    />
  );

  const hasGroups = orderedGroups.length > 0
      && orderedGroups.some((g) => (buckets[g.id] || []).length > 0);

  if (!hasGroups) {
    return <div className="space-y-4">{questions.map(renderField)}</div>;
  }

  return (
    <div className="space-y-8">
      {orderedGroups.map((g) => (
        (buckets[g.id] || []).length > 0 && (
          <section key={g.id} data-testid={`rb-section-${g.id}`} className="space-y-3">
            <div className="border-l-4 border-primary pl-3">
              <h2 className="text-lg font-semibold">{g.title}</h2>
              {g.description && (
                <p className="text-xs text-muted-foreground mt-0.5">{g.description}</p>
              )}
            </div>
            <div className="space-y-4 pl-1">
              {(buckets[g.id] || []).map(renderField)}
            </div>
          </section>
        )
      ))}
      {(buckets.__ungrouped__ || []).length > 0 && (
        <section className="space-y-3">
          {orderedGroups.length > 0 && (
            <div className="border-l-4 border-border pl-3">
              <h2 className="text-lg font-semibold text-muted-foreground">Other</h2>
            </div>
          )}
          <div className="space-y-4 pl-1">
            {buckets.__ungrouped__.map(renderField)}
          </div>
        </section>
      )}
    </div>
  );
}

function FieldRenderer({ question, value, onChange, index }) {
  const label = (
    <Label className="block mb-1 text-sm font-semibold" data-testid={`rb-fq-${question.id}-label`}>
      {index + 1}. {question.text}
      {question.required && <span className="text-rose-600 ml-1">*</span>}
    </Label>
  );
  const help = question.description && (
    <div className="text-[11px] text-muted-foreground mb-2">{question.description}</div>
  );
  const testid = `rb-fq-${question.id}-input`;

  switch (question.type) {
    case "long_text":
      return (
        <div>{label}{help}
          <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                        rows={4} className="rounded-none" data-testid={testid} />
        </div>
      );
    case "single_choice":
      return (
        <div>{label}{help}
          <div className="space-y-1" data-testid={testid}>
            {(question.options || []).map((o, i) => (
              <label key={i} className="flex items-center gap-2 border border-border p-2 cursor-pointer hover:bg-muted/40">
                <input type="radio" name={`q-${question.id}`}
                          checked={value === o.value} onChange={() => onChange(o.value)} />
                <span className="text-sm">{o.label || o.value}</span>
              </label>
            ))}
          </div>
        </div>
      );
    case "multi_choice": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (v) => onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
      return (
        <div>{label}{help}
          <div className="space-y-1" data-testid={testid}>
            {(question.options || []).map((o, i) => (
              <label key={i} className="flex items-center gap-2 border border-border p-2 cursor-pointer hover:bg-muted/40">
                <input type="checkbox" checked={arr.includes(o.value)} onChange={() => toggle(o.value)} />
                <span className="text-sm">{o.label || o.value}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }
    case "yes_no":
      return (
        <div>{label}{help}
          <div className="flex gap-2" data-testid={testid}>
            {(question.options || [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]).map((o, i) => (
              <button key={i} type="button"
                        onClick={() => onChange(o.value)}
                        className={`px-4 py-2 border ${value === o.value ? "border-primary bg-primary/10 font-semibold" : "border-border"}`}>
                {o.label || o.value}
              </button>
            ))}
          </div>
        </div>
      );
    case "rating": {
      const min = question.min_value || 1;
      const max = question.max_value || 5;
      const nums = [];
      for (let i = min; i <= max; i++) nums.push(i);
      return (
        <div>{label}{help}
          <div className="flex gap-1" data-testid={testid}>
            {nums.map((n) => (
              <button key={n} type="button" onClick={() => onChange(n)}
                        className={`w-10 h-10 border ${value === n ? "border-primary bg-primary/10 font-semibold" : "border-border"}`}>
                {n}
              </button>
            ))}
          </div>
        </div>
      );
    }
    case "date":
      return (
        <div>{label}{help}
          <Input type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                    className="rounded-none h-10 w-56" data-testid={testid} />
        </div>
      );
    case "number":
      return (
        <div>{label}{help}
          <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                    min={question.min_value} max={question.max_value}
                    className="rounded-none h-10 w-56" data-testid={testid} />
        </div>
      );
    case "file":
      return <FileField question={question} value={value} onChange={onChange}
                              testid={testid} label={label} help={help} />;
    case "short_text":
    default:
      return (
        <div>{label}{help}
          <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                    className="rounded-none h-10" data-testid={testid} />
        </div>
      );
  }
}
